import { z } from 'zod';
import { hasCover, hasGoal } from '@client/new/pages/collection/validation';
import { FormMedia, ProgramFormValues } from './programForm.types';

const GOAL_MAX = 40;
const DESCRIPTION_MAX = 2000;

// FormMedia is either null or a plain partial object. The custom refinement
// rejects strings, numbers, arrays, and other non-object junk that could
// otherwise slip through the `as unknown as` cast at the api boundary.
const formMediaSchema = z.custom<FormMedia>(
  (value) =>
    value === null ||
    (typeof value === 'object' && value !== null && !Array.isArray(value)),
  'Invalid media value',
);

export const programFormSchema = z
  .object({
    name: z.string().trim().nonempty('Name is required'),
    goal: z
      .string()
      .trim()
      .max(GOAL_MAX, `Goal must be at most ${GOAL_MAX} characters`),
    description: z
      .string()
      .max(
        DESCRIPTION_MAX,
        `Description must be at most ${DESCRIPTION_MAX} characters`,
      ),
    mediaCover: formMediaSchema,
    mediaIntro: formMediaSchema,
    color: z.string().regex(/^[0-9a-fA-F]{6}$/, 'Color must be a 6-digit hex'),
    tags: z.array(z.string()),
    targetArea: z.array(z.string()),
    requiredEquipment: z.array(z.string()),
    location: z.string(),
    difficulty: z.string(),
    contentStructure: z.enum(['sequential', 'dayweek', 'category']),
    publishingBehaviour: z.string(),
    workoutLimit: z.string(),
    durationLength: z.string(),
    durationUnit: z.string(),
    daysPerWeek: z.string(),
    sessionDuration: z.string(),
    reviewsEnabled: z.boolean(),
    reviews: z.array(
      z.object({
        name: z.string(),
        text: z.string(),
        media: formMediaSchema,
      }),
    ),
    faqsEnabled: z.boolean(),
    faqs: z.array(
      z.object({
        question: z.string(),
        answer: z.string(),
      }),
    ),
    inAppPurchaseAllowed: z.boolean(),
    swapsEnabled: z.boolean().optional(),
  })
  .superRefine((values, ctx) => {
    if (values.reviewsEnabled) {
      if (values.reviews.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['reviews'],
          message: 'Add at least one review or turn the section off',
        });
      }
      values.reviews.forEach((review, index) => {
        if (review.name.trim() === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['reviews', index, 'name'],
            message: 'This field is required',
          });
        }
        if (review.text.trim() === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['reviews', index, 'text'],
            message: 'This field is required',
          });
        }
      });
    }
    if (values.faqsEnabled) {
      if (values.faqs.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['faqs'],
          message: 'Add at least one FAQ or turn the section off',
        });
      }
      values.faqs.forEach((faq, index) => {
        if (faq.question.trim() === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['faqs', index, 'question'],
            message: 'This field is required',
          });
        }
        if (faq.answer.trim() === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['faqs', index, 'answer'],
            message: 'This field is required',
          });
        }
      });
    }
  });

// Only blocks what this edit removes, like the guard in collection.model save.
export const publishedProgramFormSchema = (persisted: ProgramFormValues) =>
  programFormSchema.superRefine((values, ctx) => {
    if (hasCover(persisted.mediaCover) && !hasCover(values.mediaCover)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['mediaCover'],
        message: 'A published program needs a cover',
      });
    }
    if (hasGoal(persisted.goal) && !hasGoal(values.goal)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['goal'],
        message: 'A published program needs a goal',
      });
    }
  });
